/// <reference types="google.maps" />
// Shared async loader for the Google Maps JS API.
// Uses `loading=async` + a global callback per Google's guidance.
// The browser key is fetched at runtime from the backend (it is a
// referrer-restricted publishable key), so it works on every domain.
let promise: Promise<typeof google> | null = null;

async function fetchBrowserKey(): Promise<string> {
  const base = import.meta.env.VITE_SUPABASE_URL;
  const anon = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const res = await fetch(`${base}/functions/v1/maps-browser-key`, {
    headers: { apikey: anon, Authorization: `Bearer ${anon}` },
  });
  if (!res.ok) throw new Error("Failed to load Google Maps key");
  const data = await res.json();
  if (!data?.key) throw new Error("Google Maps key missing");
  return data.key as string;
}

export function loadGoogleMaps(): Promise<typeof google> {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if ((window as any).google?.maps?.Map) return Promise.resolve((window as any).google);
  if (promise) return promise;

  promise = (async () => {
    const key = await fetchBrowserKey();
    await new Promise<void>((resolve, reject) => {
      const cb = `__gmapsInit_${Math.random().toString(36).slice(2)}`;
      (window as any)[cb] = () => resolve();
      const s = document.createElement("script");
      s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&loading=async&callback=${cb}`;
      s.async = true;
      s.onerror = () => reject(new Error("Failed to load Google Maps"));
      document.head.appendChild(s);
    });
    return (window as any).google;
  })();
  return promise;
}
